import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { ChevronDown, ChevronUp } from 'lucide-react';

const FAQS = [
  {
    category: 'Getting Started',
    items: [
      {
        q: 'What is PlayerIQ?',
        a: 'PlayerIQ is an AI-powered NFL fantasy analytics platform. It uses 11 seasons of historical NFL data and an XGBoost + LightGBM machine learning ensemble to generate weekly fantasy point projections for every rostered player, complete with floor/ceiling confidence intervals.',
      },
      {
        q: 'What is PPR scoring?',
        a: 'PPR stands for Points Per Reception. In PPR fantasy scoring, a player earns 1 point for every catch in addition to standard yardage and touchdown points. PlayerIQ projections are calculated using PPR scoring.',
      },
      {
        q: 'How do I start using PlayerIQ?',
        a: 'Create a free account, then head to the Dashboard. Your weekly projections are ready immediately — no setup required. Use the Demo Tour (click "Take a Tour" in the navbar) for a guided 20-step walkthrough of every feature.',
      },
    ],
  },
  {
    category: 'Projections & Risk',
    items: [
      {
        q: 'What do the Floor and Ceiling numbers mean?',
        a: 'Floor is the 10th percentile outcome — a reasonable worst case. Ceiling is the 90th percentile — a reasonable best case. These are generated by quantile regression models trained on each player\'s own historical volatility, so a boom/bust player gets a wider range than a consistent one.',
      },
      {
        q: 'What do Low / Med / High Risk labels mean?',
        a: 'Risk is based on the spread between a player\'s ceiling and floor relative to their projection. A tight range (e.g. projected 18, floor 15, ceiling 21) = Low Risk — safe, predictable. A wide range (e.g. projected 14, floor 3, ceiling 28) = High Risk — high upside but potentially volatile.',
      },
      {
        q: 'How accurate are the projections?',
        a: 'The model achieves ~70% directional accuracy (correctly predicts whether a player performs above or below their season average). Mean Absolute Error ranges from 3.6 pts (kickers) to 6.1 pts (QBs). Full accuracy metrics are available on the Accuracy page.',
      },
      {
        q: 'Why does my player show "No CI data available"?',
        a: 'Confidence intervals require at least a few games of history to compute per-player volatility. New players or those with very limited data may not have CI bounds yet.',
      },
    ],
  },
  {
    category: 'Features',
    items: [
      {
        q: 'How do I compare two players for a Start/Sit decision?',
        a: 'Go to the Compare page and search for any two players. The tool shows projected points, floor/ceiling, risk rating, and 2023–2024 historical averages side by side. You can also export the comparison to CSV.',
      },
      {
        q: 'How does the Team Builder work?',
        a: 'The Team Builder lets you build a full 15-player fantasy roster (QB, RB×2, WR×2, TE, FLEX, K, DST, plus 6 bench spots) using live projections. Click a slot to activate it, then pick from the player pool. Your team\'s projected total, floor, and ceiling update in real time. You can save and reload your lineups.',
      },
      {
        q: 'What is the Analytics page?',
        a: 'The Analytics page covers historical NFL data from 2015–2025. You can browse a season leaderboard by position, search for any player, and view a week-by-week trend chart overlaid with the current ML projection.',
      },
      {
        q: 'How do I favorite a player?',
        a: 'Click the star icon on any player row in the Dashboard. Favorites are saved to your account and persist across sessions. You can filter the table to show only favorites using the filter controls.',
      },
      {
        q: 'Can I export data?',
        a: 'Yes — the Compare page has a "Export CSV" button that downloads a full comparison including projected stats and 2-year historical averages for both players.',
      },
    ],
  },
  {
    category: 'Account & Security',
    items: [
      {
        q: 'How do I reset my password?',
        a: 'Click "Forgot password?" on the login page. Enter your email and you\'ll receive a reset link valid for 30 minutes. Check your spam folder if it doesn\'t arrive within a few minutes.',
      },
      {
        q: 'How long does my session last?',
        a: 'Sessions last 24 hours from login, or 30 minutes of inactivity — whichever comes first. After that you\'ll be redirected to the login page automatically.',
      },
      {
        q: 'Is my data secure?',
        a: 'Yes. Passwords are hashed with bcrypt before storage and are never readable. All traffic is encrypted over HTTPS. Authentication uses JWTs with a 24-hour expiry. Login and registration are rate-limited to prevent brute-force attacks.',
      },
    ],
  },
  {
    category: 'Model & Data',
    items: [
      {
        q: 'Where does the data come from?',
        a: 'Historical weekly stats come from NFLFastR (2015–2025), an open-source NFL play-by-play data package. Live injury status comes from the Sleeper API, updated weekly. Player headshots are sourced from ESPN.',
      },
      {
        q: 'How often are projections updated?',
        a: 'Projections are refreshed every Tuesday via an automated pipeline that ingests the latest week\'s stats, reruns feature engineering, and generates new predictions. The Dashboard shows a "Last Updated" timestamp.',
      },
      {
        q: 'What positions are supported?',
        a: 'QB, RB, WR, TE, K, and DST. Each position has its own dedicated ML model trained on position-specific features — for example, RB models weight backfield share heavily, while WR/TE models prioritize target share and reception volume.',
      },
      {
        q: 'What does the Accuracy page show?',
        a: 'The Accuracy page shows real MAE (Mean Absolute Error) and RMSE (Root Mean Squared Error) per position and per week for held-out test data. This is the honest performance of the model on data it never trained on — not in-sample results.',
      },
    ],
  },
];

const FAQItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/[0.06] last:border-0">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-4 py-4 text-left hover:text-white transition-colors"
      >
        <span className={`text-sm font-semibold leading-snug ${open ? 'text-white' : 'text-white/70'}`}>{q}</span>
        {open
          ? <ChevronUp size={15} className="text-[#4d7cff] flex-shrink-0" />
          : <ChevronDown size={15} className="text-white/30 flex-shrink-0" />}
      </button>
      {open && (
        <p className="text-sm text-white/50 leading-relaxed pb-4 pr-6">{a}</p>
      )}
    </div>
  );
};

const Help = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#050d1f] text-white font-sans">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 pt-10 pb-24">

        <div className="mb-10">
          <h1 className="text-4xl font-black tracking-tight mb-2">
            Help <span className="text-[#4d7cff]">Center</span>
          </h1>
          <p className="text-white/40">Everything you need to get the most out of PlayerIQ.</p>
        </div>

        {/* Quick links */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-12">
          {[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'Compare',   path: '/compare' },
            { label: 'Team Builder', path: '/team-builder' },
            { label: 'Accuracy', path: '/accuracy' },
          ].map(({ label, path }) => (
            <button key={path} onClick={() => navigate(path)}
              className="py-3 rounded-xl bg-white/[0.04] border border-white/[0.08] text-sm font-semibold text-white/60 hover:text-[#4d7cff] hover:border-[#4d7cff]/30 transition-all">
              {label}
            </button>
          ))}
        </div>

        {/* FAQ sections */}
        <div className="space-y-8">
          {FAQS.map(({ category, items }) => (
            <div key={category}>
              <h2 className="text-xs font-bold uppercase tracking-widest text-[#4d7cff] mb-3">{category}</h2>
              <div className="bg-[#0c1526] border border-white/[0.08] rounded-2xl px-5">
                {items.map(item => <FAQItem key={item.q} {...item} />)}
              </div>
            </div>
          ))}
        </div>

        {/* Footer CTA */}
        <div className="mt-12 text-center text-sm text-white/30">
          Still have questions?{' '}
          <button onClick={() => navigate('/dashboard')}
            className="text-[#4d7cff] hover:underline">
            Take the guided demo tour
          </button>
          {' '}— it walks through every feature live.
        </div>

      </main>
    </div>
  );
};

export default Help;
